// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

// Lazy pdfjs-dist loader. The provider form bundles via Vite at the app
// level (apps/provider-ui), so the dynamic import + worker URL pattern
// resolves at build time. The worker is fetched only the first time the
// user enters the Upload Documents step.


export interface PdfTextItem {
  str: string;
  /** x position in PDF user-space units (left-origin) */
  x: number;
  /** y position in PDF user-space units (bottom-origin) */
  y: number;
  width: number;
  height: number;
  fontName?: string;
}

export interface PdfPage {
  pageNumber: number;
  items: PdfTextItem[];
  /** Concatenated plain text in reading order (for regex anchors) */
  text: string;
}

export interface LoadedPdf {
  numPages: number;
  pages: PdfPage[];
}

let workerInitialised = false;

async function ensureWorker(pdfjs: any): Promise<void> {
  if (workerInitialised) return;
  try {
    // Vite will rewrite this to a hashed asset URL at build time. The
    // `?url` suffix asks the bundler to give us the URL string instead
    // of the module's exports.
    // @ts-ignore — bundler-specific virtual import, resolved by Vite
    const mod = await import('pdfjs-dist/build/pdf.worker.min.mjs?url');
    const workerUrl = (mod as { default: string }).default;
    pdfjs.GlobalWorkerOptions.workerSrc = workerUrl;
  } catch {
    // Fallback: pin to the matching CDN-hosted worker. This is only hit
    // when the bundler does not understand `?url` (e.g. plain `tsc` or
    // SSR). In production it is overwritten by the Vite asset URL above.
    pdfjs.GlobalWorkerOptions.workerSrc =
      'https://cdn.jsdelivr.net/npm/pdfjs-dist@6.0.227/build/pdf.worker.min.mjs';
  }
  workerInitialised = true;
}

/**
 * Parses a PDF file into per-page positional text items. Items keep their
 * original PDF user-space coordinates (origin bottom-left) so callers can
 * group by row (`y`) and column (`x`) — essential for the transactions
 * table parser.
 */
export async function loadPdf(file: File): Promise<LoadedPdf> {
  // Dynamic import keeps pdfjs-dist (~300 KB) out of the main bundle.
  const pdfjs = await import('pdfjs-dist');
  await ensureWorker(pdfjs);

  const arrayBuffer = await file.arrayBuffer();
  const loadingTask = pdfjs.getDocument({
    data: arrayBuffer,
    // isEvalSupported was removed from DocumentInitParameters in
    // pdfjs-dist v6 — eval was excised from the codebase upstream.
    disableAutoFetch: true,
    disableStream: true,
  });
  const doc = await loadingTask.promise;

  const pages: PdfPage[] = [];
  for (let pageNumber = 1; pageNumber <= doc.numPages; pageNumber++) {
    const page = await doc.getPage(pageNumber);
    const content = await page.getTextContent();
    const items: PdfTextItem[] = [];
    const textParts: string[] = [];
    for (const raw of content.items as any[]) {
      if (typeof raw.str !== 'string') continue;
      // pdfjs returns a 6-element transform matrix [a, b, c, d, e, f].
      // For regular horizontal text (a≈d, b=c=0) the position is (e, f)
      // and the height is `d`.
      const [a, , , d, e, f] = raw.transform as number[];
      items.push({
        str: raw.str,
        x: e,
        y: f,
        width: raw.width,
        height: Math.abs(d || a),
        fontName: raw.fontName,
      });
      textParts.push(raw.str);
    }
    pages.push({
      pageNumber,
      items,
      text: textParts.join(' '),
    });
  }

  return { numPages: doc.numPages, pages };
}

/** Group text items into rows by Y coordinate, then sort by X within each row. */
export function groupItemsByRow(
  items: PdfTextItem[],
  yTolerance = 2,
): PdfTextItem[][] {
  const sorted = [...items].sort((a, b) => b.y - a.y || a.x - b.x);
  const rows: PdfTextItem[][] = [];
  for (const item of sorted) {
    const last = rows[rows.length - 1];
    if (last && Math.abs(last[0].y - item.y) <= yTolerance) {
      last.push(item);
    } else {
      rows.push([item]);
    }
  }
  for (const row of rows) {
    row.sort((a, b) => a.x - b.x);
  }
  return rows;
}

/** Concatenate the joined text of all pages — useful for fingerprinting. */
export function fullText(pdf: LoadedPdf): string {
  return pdf.pages.map((p) => p.text).join('\n');
}
